import { useState, useRef, useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { congressTradesService } from '../services/congressTradesService';
import type { CongressTrade, SortField, SortDirection } from '../types/congressTrades';
import { TZ } from '../utils/marketTime';

interface CongressTradesWidgetProps {
  onRemove: () => void;
  ticker?: string;
}

// Get member name from trade data
const getMemberName = (trade: CongressTrade): string => {
  if (trade.representative_name) {
    return trade.representative_name;
  }
  if (trade.senator_first_name || trade.senator_last_name) {
    return `${trade.senator_first_name || ''} ${trade.senator_last_name || ''}`.trim();
  }
  return 'Unknown';
};

// Format date to "Jan 15, 2024" format
const formatDate = (dateStr: string): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { timeZone: TZ, month: 'short', day: 'numeric', year: 'numeric' });
};

// Short date format for compact display
const formatDateShort = (dateStr: string): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { timeZone: TZ, month: 'short', day: 'numeric' });
};

// Transaction type badge colors
const getTransactionStyle = (type: string): { bg: string; text: string } => {
  const typeLower = type?.toLowerCase() || '';
  if (typeLower === 'purchase') {
    return { bg: 'bg-bull-blue/20', text: 'text-bull-blue' };
  }
  if (typeLower === 'sale') {
    return { bg: 'bg-bear-red/20', text: 'text-bear-red' };
  }
  // exchange, receive, etc.
  return { bg: 'bg-accent-yellow/20', text: 'text-accent-yellow' };
};

// Format transaction type for display
const formatTransactionType = (type: string): string => {
  if (!type) return '-';
  return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
};

export function CongressTradesWidget({ onRemove, ticker }: CongressTradesWidgetProps) {
  const [search, setSearch] = useState(ticker?.toUpperCase() ?? '');
  const [searchInput, setSearchInput] = useState(ticker?.toUpperCase() ?? '');
  const [sortBy, setSortBy] = useState<SortField>('disclosure_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const sentinelRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['congressTrades', search, sortBy, sortDirection],
      queryFn: ({ pageParam }) =>
        congressTradesService.fetchTrades({
          page: pageParam as number,
          perPage: 20,
          search,
          sortBy,
          sortDirection,
        }),
      getNextPageParam: (lastPage) =>
        lastPage.page < lastPage.total_pages ? lastPage.page + 1 : undefined,
      initialPageParam: 1,
      staleTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    });

  const allTrades = data?.pages.flatMap((p) => p.results) ?? [];

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field) {
      return <span className="text-gray-600 ml-1">↕</span>;
    }
    return <span className="text-bull-blue ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
      {/* Title Bar - drag handle */}
      <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move relative z-20">
        <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">Congress Trades</h3>
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-white hover:bg-bear-red/20 text-sm px-2 py-0.5 rounded transition-colors"
          title="Close widget"
        >
          ×
        </button>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="px-3 py-2 border-b border-dark-border">
        <div className="flex gap-2">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name or symbol..."
            className="flex-1 px-2 py-1 text-xs bg-dark-surface border border-dark-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-accent-yellow"
          />
          <button
            type="submit"
            className="px-3 py-1 text-xs bg-dark-surface-light hover:bg-dark-border text-white rounded transition-colors"
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                setSearchInput('');
              }}
              className="px-2 py-1 text-xs text-gray-400 hover:text-white transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </form>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {isLoading && (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            Loading...
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-full text-bear-red text-sm">
            Error loading data
          </div>
        )}

        {allTrades.length > 0 && (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-dark-surface-light border-b border-dark-border">
              <tr>
                <th
                  className="px-2 py-1.5 text-left font-semibold text-gray-300 cursor-pointer hover:text-white"
                  onClick={() => handleSort('disclosure_date')}
                >
                  Disclosed <SortIcon field="disclosure_date" />
                </th>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-300">Member</th>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-300">Symbol</th>
                <th className="px-2 py-1.5 text-left font-semibold text-gray-300">Type</th>
                <th className="px-2 py-1.5 text-right font-semibold text-gray-300">Amount</th>
              </tr>
            </thead>
            <tbody>
              {allTrades.map((trade: CongressTrade, index: number) => {
                const txStyle = getTransactionStyle(trade.transaction_type);
                return (
                  <tr
                    key={`${trade.instrument_id}-${trade.disclosure_date}-${index}`}
                    className="border-b border-dark-border hover:bg-dark-surface-light transition-colors"
                  >
                    <td className="px-2 py-1.5 text-gray-400" title={`Transaction: ${formatDate(trade.transaction_date)}`}>
                      {formatDateShort(trade.disclosure_date)}
                    </td>
                    <td className="px-2 py-1.5 text-white truncate max-w-[120px]" title={getMemberName(trade)}>
                      {getMemberName(trade)}
                    </td>
                    <td className="px-2 py-1.5">
                      <a
                        href={`/ticker/${trade.instrument_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-white hover:text-bull-blue transition-colors"
                      >
                        {trade.instrument_id}
                      </a>
                    </td>
                    <td className="px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-xs ${txStyle.bg} ${txStyle.text}`}>
                        {formatTransactionType(trade.transaction_type)}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-right font-numeric text-white">
                      {trade.amount_range || '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {!isLoading && allTrades.length === 0 && (
          <div className="flex items-center justify-center h-full text-gray-500 text-sm">
            {search ? 'No results found' : 'No trades available'}
          </div>
        )}

        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} className="h-6 flex items-center justify-center">
          {isFetchingNextPage && (
            <span className="text-xs text-gray-600">Loading more...</span>
          )}
        </div>
      </div>
    </div>
  );
}
